import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Table, Tabs, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import AnalyticsPage from '../../shared-components/analytics/AnalyticsPage';
import StatTile from '../../shared-components/analytics/StatTile';
import BarList from '../../shared-components/charts/BarList';
import { getUnmapped, getZeroCost, getCatalogDrift } from '../../../analytics/analyticsApi';
import { thousand, toCurrency } from '../../../functions';

// The unglamorous screen with the highest leverage: every row here is one
// mapping insert away from correcting all history retroactively, and the list
// arrives already sorted by revenue at stake.
function AnalyticsQuality() {
  const { t } = useTranslation();
  const [unmapped, setUnmapped] = useState(null);
  const [zeroCost, setZeroCost] = useState(null);
  const [drift, setDrift] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getUnmapped({}), getZeroCost({}), getCatalogDrift({})])
      .then(([u, z, d]) => { setUnmapped(u); setZeroCost(z); setDrift(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const uRows = unmapped?.items ?? [];
  const zRows = zeroCost?.items ?? [];
  const dRows = drift?.items ?? [];


  const TabIntro = ({ text }) => (
    <div className="mb-4 rounded-lg border border-line bg-gold-50/60 px-3 py-2.5 text-xs leading-relaxed text-ink">
      {text}
    </div>
  );

  const revenueBar = (rows, labelFn) => (
    <BarList
      rows={_.map(rows.slice(0, 8), (r, i) => ({ id: i, label: labelFn(r), value: r.revenueImpact }))}
      formatValue={(v) => toCurrency(v)}
    />
  );

  return (
    <AnalyticsPage
      title={t('analytics_quality_title')}
      subtitle={t('analytics_quality_subtitle')}
    >
      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={t('analytics_mapping_backlog')}
          value={thousand(uRows.length)}
          sub={toCurrency(_.sumBy(uRows, 'revenueImpact'))}
          emphasis
          hint={t('analytics_backlog_help')}
        />
        <StatTile
          label={t('analytics_zero_cost_products')}
          value={thousand(zRows.length)}
          sub={toCurrency(_.sumBy(zRows, 'revenueImpact'))}
          hint={t('analytics_zero_cost_help')}
        />
        <StatTile
          label={t('analytics_catalog_drift')}
          value={thousand(dRows.length)}
          sub={toCurrency(_.sumBy(dRows, 'revenueImpact'))}
          hint={t('analytics_drift_help')}
        />
        <StatTile
          label={t('analytics_revenue_at_stake')}
          value={toCurrency(
            _.sumBy(uRows, 'revenueImpact')
            + _.sumBy(zRows, 'revenueImpact')
            + _.sumBy(dRows, 'revenueImpact')
          )}
        />
      </div>

      <div className="rounded-card border border-line bg-surface p-4 shadow-card">
        <Tabs
          items={[
            {
              key: 'unmapped',
              label: t('analytics_unmapped_tab'),
              children: (
                <>
                <TabIntro text={t('analytics_unmapped_desc')} />
                <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  <div>
                    <div className="mb-3 text-sm font-semibold text-ink">
                      {t('analytics_revenue_at_stake')}
                    </div>
                    {revenueBar(uRows, (r) => r.rawValue || t('analytics_blank_value'))}
                  </div>
                  <Table
                    size="small"
                    bordered={false}
                    loading={loading}
                    rowKey="rawKey"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    dataSource={uRows}
                    locale={{ emptyText: t('no_data') }}
                    columns={[
                      {
                        title: t('analytics_raw_value'),
                        key: 'raw',
                        render: (r) => (
                          <span className="inline-flex items-center gap-2">
                            <span className="font-mono text-sm text-ink">
                              {r.rawValue || <em className="text-muted">{t('analytics_blank_value')}</em>}
                            </span>
                            <Tag className="m-0">{r.levelCode}</Tag>
                          </span>
                        ),
                      },
                      {
                        title: t('analytics_store'),
                        key: 'store',
                        render: (r) => (
                          <span className="text-xs text-muted">
                            {r.businessId ? `#${r.businessId}` : t('analytics_global')}
                          </span>
                        ),
                      },
                      {
                        title: t('analytics_products'),
                        key: 'products',
                        align: 'right',
                        render: (r) => <span className="tabular-nums">{thousand(r.distinctProductCount)}</span>,
                      },
                      {
                        title: t('analytics_last_seen'),
                        key: 'seen',
                        align: 'right',
                        render: (r) => (
                          <span className="tabular-nums text-muted">
                            {r.lastSeenAtUtc ? dayjs(r.lastSeenAtUtc).format('DD MMM YYYY') : '—'}
                          </span>
                        ),
                      },
                      {
                        title: t('analytics_revenue_impact'),
                        key: 'impact',
                        align: 'right',
                        render: (r) => (
                          <span className="tabular-nums font-semibold text-ink">
                            {toCurrency(r.revenueImpact)}
                          </span>
                        ),
                      },
                    ]}
                  />
                </div>
                </>
              ),
            },
            {
              key: 'zerocost',
              label: t('analytics_zero_cost_tab'),
              children: (
                <>
                <TabIntro text={t('analytics_zero_cost_desc')} />
                <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  <div>
                    <div className="mb-3 text-sm font-semibold text-ink">
                      {t('analytics_revenue_at_stake')}
                    </div>
                    {revenueBar(zRows, (r) => r.productName)}
                  </div>
                  <Table
                    size="small"
                    bordered={false}
                    loading={loading}
                    rowKey="businessItemId"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                    dataSource={zRows}
                    locale={{ emptyText: t('no_data') }}
                    columns={[
                      {
                        title: t('product'),
                        key: 'name',
                        render: (r) => (
                          <div>
                            <div className="text-sm font-semibold text-ink">{r.productName}</div>
                            <div className="text-xs tabular-nums text-muted">{r.itemCode}</div>
                          </div>
                        ),
                      },
                      {
                        title: t('analytics_department'),
                        key: 'dept',
                        render: (r) => <span className="text-xs text-muted">{r.departmentName}</span>,
                      },
                      {
                        title: t('analytics_zero_cost_lines'),
                        key: 'lines',
                        align: 'right',
                        render: (r) => (
                          <span className="tabular-nums">
                            {thousand(r.zeroCostLineCount)}
                            <span className="text-muted"> / {thousand(r.salesLineCount)}</span>
                          </span>
                        ),
                      },
                      {
                        title: t('analytics_revenue_impact'),
                        key: 'impact',
                        align: 'right',
                        render: (r) => (
                          <span className="tabular-nums font-semibold text-ink">
                            {toCurrency(r.revenueImpact)}
                          </span>
                        ),
                      },
                    ]}
                  />
                </div>
                </>
              ),
            },
            {
              key: 'drift',
              label: t('analytics_drift_tab'),
              children: (
                <>
                <TabIntro text={t('analytics_drift_desc')} />
                <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
                  <div>
                    <div className="mb-3 text-sm font-semibold text-ink">
                      {t('analytics_revenue_at_stake')}
                    </div>
                    {revenueBar(dRows, (r) => r.productName)}
                  </div>
                  <div>
                    <Table
                      size="small"
                      bordered={false}
                      loading={loading}
                      rowKey="businessItemId"
                      pagination={false}
                      scroll={{ x: 'max-content' }}
                      dataSource={dRows}
                      locale={{ emptyText: t('no_data') }}
                      columns={[
                        {
                          title: t('product'),
                          key: 'name',
                          render: (r) => (
                            <div>
                              <div className="text-sm font-semibold text-ink">{r.productName}</div>
                              <div className="text-xs tabular-nums text-muted">{r.itemCode}</div>
                            </div>
                          ),
                        },
                        {
                          title: t('analytics_drifted_lines'),
                          key: 'drifted',
                          align: 'right',
                          render: (r) => <span className="tabular-nums">{thousand(r.driftedLineCount)}</span>,
                        },
                        {
                          title: t('analytics_not_in_catalog'),
                          key: 'missing',
                          align: 'right',
                          render: (r) => (
                            <span className="tabular-nums">
                              {r.notInCatalogLineCount > 0
                                ? <Tag color="error" className="m-0">{thousand(r.notInCatalogLineCount)}</Tag>
                                : <span className="text-muted">—</span>}
                            </span>
                          ),
                        },
                        {
                          title: t('analytics_revenue_impact'),
                          key: 'impact',
                          align: 'right',
                          render: (r) => (
                            <span className="tabular-nums font-semibold text-ink">
                              {toCurrency(r.revenueImpact)}
                            </span>
                          ),
                        },
                      ]}
                    />
                    <p className="mt-3 text-xs leading-relaxed text-muted">
                      {t('analytics_as_was_note')}
                    </p>
                  </div>
                </div>
                </>
              ),
            },
          ]}
        />
      </div>
    </AnalyticsPage>
  );
}

export default AnalyticsQuality;
